import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Edit, Trash2, Activity, Settings } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface CustomActivityType {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  category: string;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

interface ActivityTypeFormData {
  name: string;
  description: string;
  icon: string;
  color: string;
  category: string;
}

const CATEGORIES = [
  'Sales',
  'Marketing', 
  'Client Relations',
  'Internal',
  'Project Management',
  'Communication',
  'Follow-up',
  'Other'
];

const COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#EC4899', // Pink
  '#6B7280'  // Gray
];

export function CustomActivityTypeManager() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<CustomActivityType | null>(null);
  const [formData, setFormData] = useState<ActivityTypeFormData>({
    name: '',
    description: '',
    icon: 'activity',
    color: COLORS[0],
    category: CATEGORIES[0]
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch custom activity types
  const { data: activityTypes, isLoading } = useQuery({
    queryKey: ['customActivityTypes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('custom_activity_types')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });
      
      if (error) throw error;
      return data as CustomActivityType[];
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      icon: 'activity',
      color: COLORS[0],
      category: CATEGORIES[0]
    });
    setEditingType(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (activityType: CustomActivityType) => {
    setFormData({
      name: activityType.name,
      description: activityType.description,
      icon: activityType.icon,
      color: activityType.color,
      category: activityType.category
    });
    setEditingType(activityType);
    setIsCreateDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Activity type name is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingType) {
        // Update existing activity type
        const { error } = await supabase
          .from('custom_activity_types')
          .update({
            name: formData.name.trim(),
            description: formData.description.trim(),
            icon: formData.icon,
            color: formData.color,
            category: formData.category,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingType.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Activity type updated successfully.",
        });
      } else {
        // Create new activity type
        const { error } = await supabase
          .from('custom_activity_types')
          .insert([{
            name: formData.name.trim(),
            description: formData.description.trim(),
            icon: formData.icon,
            color: formData.color,
            category: formData.category,
            is_system: false
          }]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Activity type created successfully.",
        });
      }

      // Refresh the data
      queryClient.invalidateQueries({ queryKey: ['customActivityTypes'] });
      
      // Close dialog and reset form
      setIsCreateDialogOpen(false);
      resetForm();

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save activity type. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (activityType: CustomActivityType) => {
    if (activityType.is_system) {
      toast({
        title: "Cannot Delete",
        description: "System activity types cannot be deleted.",
        variant: "destructive",
      });
      return;
    }

    if (!confirm(`Are you sure you want to delete "${activityType.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('custom_activity_types')
        .delete()
        .eq('id', activityType.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Activity type deleted successfully.",
      });

      // Refresh the data
      queryClient.invalidateQueries({ queryKey: ['customActivityTypes'] });

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete activity type. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Group activity types by category
  const groupedActivityTypes = activityTypes?.reduce((acc, type) => {
    const category = type.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(type);
    return acc;
  }, {} as Record<string, CustomActivityType[]>) || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Custom Activity Types</h2>
          <p className="text-muted-foreground">
            Manage custom activity types for your team to use when creating activities.
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add Activity Type
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          {Array.from({ length: 3 }).map((_, categoryIndex) => (
            <Card key={categoryIndex}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: 6 }).map((_, typeIndex) => (
                    <div
                      key={typeIndex}
                      className="border rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Skeleton className="w-6 h-6 rounded" />
                          <Skeleton className="h-4 w-24" />
                        </div>
                        <Skeleton className="h-5 w-12 rounded-full" />
                      </div>
                      <Skeleton className="h-3 w-full mb-3" />
                      <div className="flex gap-2">
                        <Skeleton className="h-8 w-8 rounded" />
                        <Skeleton className="h-8 w-8 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedActivityTypes).map(([category, types]) => (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="text-lg">{category}</CardTitle>
                <CardDescription>
                  {types.length} activity type{types.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {types.map((type) => (
                    <div
                      key={type.id}
                      className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded flex items-center justify-center text-white text-xs"
                            style={{ backgroundColor: type.color }}
                          >
                            <Activity className="h-3 w-3" />
                          </div>
                          <h4 className="font-medium">{type.name}</h4>
                        </div>
                        <div className="flex gap-1">
                          {type.is_system && (
                            <Badge variant="secondary" className="text-xs">
                              System
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {type.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {type.description}
                        </p>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(type)}
                          disabled={type.is_system}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(type)}
                          disabled={type.is_system}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          {Object.keys(groupedActivityTypes).length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <div className="flex flex-col items-center space-y-4">
                  <Activity className="h-16 w-16 text-muted-foreground/50" />
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold">No Custom Activity Types</h3>
                    <p className="text-muted-foreground max-w-md">
                      Customize your activity tracking by creating specific activity types that match your workflow. Examples: Client Check-ins, Project Reviews, Sales Calls, etc.
                    </p>
                  </div>
                  <Button onClick={openCreateDialog} size="lg" className="mt-4">
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First Activity Type
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingType ? 'Edit Activity Type' : 'Create Activity Type'}
            </DialogTitle>
            <DialogDescription>
              {editingType 
                ? 'Update the activity type details below.' 
                : 'Create a new custom activity type for your team to use.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Client Check-in Call"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of this activity type"
                rows={2}
              />
            </div>
            
            <div>
              <Label>Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({...formData, category: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(category => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Color</Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({...formData, color})}
                    className={`w-8 h-8 rounded border-2 ${
                      formData.color === color ? 'border-primary' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button type="submit">
                {editingType ? 'Update' : 'Create'} Activity Type
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}